import React, { useState, useContext, useRef, useEffect } from 'react';
import './styles/Header.css';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { app } from './firebaseAuth';
import { UserContext } from '../UserContext';
import '@fortawesome/fontawesome-free/css/all.min.css';
import logout from '../assets/logout.png';
import white from '../assets/white.png';
import Cookies from 'universal-cookie';
import { resolveSecureFileUrl } from '../utils/fileAccess';
import { FaBell } from 'react-icons/fa';
import { FiEdit2, FiTrash2, FiUser, FiFileText } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { FaCloudUploadAlt } from "react-icons/fa";
import { notifyError, notifySuccess } from '../components/ErrorAlert';
import { openSecureFileUrl } from '../utils/fileAccess';
import { FiMapPin } from "react-icons/fi";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { HEADER_MESSAGES } from "../constants/headerMessages";
import IndividualModal from "./IndividualModal";
import AddressModal from "./AddressModal";

const cookies = new Cookies();
const NOTIFICATION_REFRESH_INTERVAL_MS = 10000;
const getDobPreview = (dobValue) => (dobValue ? String(dobValue).split('T')[0] : '');
const getSavedIndividualsStorageKey = (email = '') =>
  `routeplanner:saved-individuals:${String(email || '').trim().toLowerCase()}`;

const Header = () => {
  const { user, setUser, token, setToken, setRefreshToken, setSessionStartedAt } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileImageSrc, setProfileImageSrc] = useState('');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [savedInfoOpen, setSavedInfoOpen] = useState(false);
  const [savedInfoSearch, setSavedInfoSearch] = useState('');
  const [activeSavedInfoTab, setActiveSavedInfoTab] = useState('individuals');
  const [savedIndividuals, setSavedIndividuals] = useState([]);
  const [savedInfoSelections, setSavedInfoSelections] = useState([]);
  const [savedInfoDeleteTarget, setSavedInfoDeleteTarget] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedAddressSearch, setSavedAddressSearch] = useState("");
  const [savedAddressDeleteTarget, setSavedAddressDeleteTarget] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressModalMode, setAddressModalMode] = useState("create");

  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [individualModalMode, setIndividualModalMode] = useState("create");
  const [editingIndividual, setEditingIndividual] = useState(null);
  const libraries = ["places"];

  const { isLoaded } = useJsApiLoader({
    id: 'script-loader',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
    region: "IN",
    language: "en",
  });

  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const notificationsRef = useRef(null);
  const savedInfoRef = useRef(null);

  const handleClick = async () => {
    const auth = getAuth(app);

    await signOut(auth)
      .then(() => {
        setUser(null);
        setToken('');
        setRefreshToken('');
        setSessionStartedAt('');

        cookies.remove('user', { path: '/' });
        cookies.remove('token', { path: '/' });
        cookies.remove('refreshToken', { path: '/' });
        cookies.remove('sessionStartedAt', { path: '/' });

        navigate('/login');
      })
      .catch((err) => {
        console.error("error", err);
      });
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const fetchSavedIndividuals = async (authToken = token) => {
    const storageKey = getSavedIndividualsStorageKey(user?.email);
    const cachedSavedIndividuals = (() => {
      try {
        return JSON.parse(localStorage.getItem(storageKey) || '[]');
      } catch (error) {
        return [];
      }
    })();

    if (cachedSavedIndividuals.length) {
      setSavedIndividuals(cachedSavedIndividuals);
    }

    if (!authToken) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/saved-individuals`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText ||
            HEADER_MESSAGES.FETCH_SAVED_INDIVIDUALS_FAILED
        );
      }

      const data = await response.json();
      setSavedIndividuals(Array.isArray(data) ? data : []);
      localStorage.setItem(storageKey, JSON.stringify(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Failed to fetch saved individuals:', error);
      setSavedIndividuals(cachedSavedIndividuals);
    }
  };

  const fetchSavedAddresses = async (authToken = token) => {
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/saved-addresses`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          HEADER_MESSAGES.FETCH_SAVED_ADDRESSES_FAILED
        );
      }

      const data = await response.json();

      setSavedAddresses(Array.isArray(data) ? data : []);

    } catch (err) {
      console.error("Failed to fetch saved addresses:", err);
    }
  };

  const fetchNotifications = async (authToken = token) => {
    if (!authToken) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notifications`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          HEADER_MESSAGES.FETCH_NOTIFICATIONS_FAILED
        );
      }

      const data = await response.json();
      setNotifications(data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          HEADER_MESSAGES.MARK_NOTIFICATIONS_READ_FAILED
        );
      }

      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const toggleNotifications = async () => {
    const nextOpen = !notificationsOpen;
    setNotificationsOpen(nextOpen);
    setDropdownOpen(false);
    setSavedInfoOpen(false);

    if (nextOpen) {
      await fetchNotifications();
    }
  };

  const toggleSavedInfo = async () => {
    const nextOpen = !savedInfoOpen;
    setSavedInfoOpen(nextOpen);
    setDropdownOpen(false);
    setNotificationsOpen(false);

    if (nextOpen) {
      await fetchSavedIndividuals();
      await fetchSavedAddresses();
    }
  };

  const filteredSavedIndividuals = savedIndividuals.filter((individual) =>
    [individual.name, individual.idNumber, individual.contactNumber]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(savedInfoSearch.trim().toLowerCase())
      )
  );

  const filteredSavedAddresses = savedAddresses.filter((item) =>
    item.address
      .toLowerCase()
      .includes(savedAddressSearch.toLowerCase())
  );

  const handleAddNewIndividual = () => {
    setSavedInfoOpen(false);
    setDropdownOpen(false);

    setEditingIndividual(null);
    setIndividualModalMode("create");
    setIndividualModalOpen(true);
  };

  const handleAddNewAddress = () => {
    setSavedInfoOpen(false);
    setDropdownOpen(false);

    setEditingAddress(null);
    setAddressModalMode("create");
    setAddressModalOpen(true);
  };

  const askDeleteSavedInfo = (individual) => {
    setSavedInfoDeleteTarget(individual);
  };

  const handleConfirmDeleteSavedInfo = async () => {
    if (!savedInfoDeleteTarget || !token) {
      setSavedInfoDeleteTarget(null);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/saved-individuals/${savedInfoDeleteTarget.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText ||
            HEADER_MESSAGES.DELETE_SAVED_INFO_FAILED
        );
      }

      const nextSavedIndividuals = savedIndividuals.filter(
        (individual) => individual.id !== savedInfoDeleteTarget.id
      );
      setSavedIndividuals(nextSavedIndividuals);
      localStorage.setItem(
        getSavedIndividualsStorageKey(user?.email),
        JSON.stringify(nextSavedIndividuals)
      );
      setSavedInfoSelections((prev) => prev.filter((id) => id !== savedInfoDeleteTarget.id));
      window.dispatchEvent(
        new CustomEvent('routeplanner:saved-individuals-updated', {
          detail: { savedIndividuals: nextSavedIndividuals },
        })
      );
    } catch (error) {
      console.error('Failed to delete saved individual:', error);
    } finally {
      setSavedInfoDeleteTarget(null);
    }
  };

  const handleConfirmDeleteSavedAddress = async () => {
    if (!savedAddressDeleteTarget) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/saved-addresses/${savedAddressDeleteTarget.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          HEADER_MESSAGES.DELETE_ADDRESS_FAILED
        );
      }

      const updated = savedAddresses.filter(
        item => item.id !== savedAddressDeleteTarget.id
      );

      setSavedAddresses(updated);

      window.dispatchEvent(
        new CustomEvent("routeplanner:saved-addresses-updated", {
          detail: {
            savedAddresses: updated,
          },
        })
      );

      notifySuccess(HEADER_MESSAGES.ADDRESS_DELETED);
    } catch (err) {
      notifyError(
        err.message ||
           HEADER_MESSAGES.DELETE_ADDRESS_FAILED
      );
    } finally {
      setSavedAddressDeleteTarget(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".user-pic")
      ) {
        setDropdownOpen(false);
      }

      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target) &&
        !event.target.closest('.notification-bell')
      ) {
        setNotificationsOpen(false);
      }

      if (
        savedInfoRef.current &&
        !savedInfoRef.current.contains(event.target) &&
        !event.target.closest('.saved-info-trigger')
      ) {
        setSavedInfoOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchNotifications(token);
  }, [token]);

  useEffect(() => {
    fetchSavedIndividuals(token);
    fetchSavedAddresses(token);
  }, [token]);

  useEffect(() => {
    const handleSavedIndividualsUpdated = (event) => {
      const nextSavedIndividuals = event.detail?.savedIndividuals;
      if (Array.isArray(nextSavedIndividuals)) {
        setSavedIndividuals(nextSavedIndividuals);
        localStorage.setItem(
          getSavedIndividualsStorageKey(user?.email),
          JSON.stringify(nextSavedIndividuals)
        );
      } else {
        fetchSavedIndividuals(token);
      }
    };

    window.addEventListener('routeplanner:saved-individuals-updated', handleSavedIndividualsUpdated);
    return () => {
      window.removeEventListener('routeplanner:saved-individuals-updated', handleSavedIndividualsUpdated);
      };
    }, [token]);

  useEffect(() => {

    const handleSavedAddressesUpdated = (event) => {

      const nextSavedAddresses = event.detail?.savedAddresses;

      if (Array.isArray(nextSavedAddresses)) {
        setSavedAddresses(nextSavedAddresses);
      } else {
        fetchSavedAddresses(token);
      }

    };

    window.addEventListener(
      "routeplanner:saved-addresses-updated",
      handleSavedAddressesUpdated
    );

    return () => {

      window.removeEventListener(
        "routeplanner:saved-addresses-updated",
        handleSavedAddressesUpdated
      );

    };

  }, [token]);

  useEffect(() => {
    if (!token) return;

    const intervalId = window.setInterval(() => {
      fetchNotifications(token);
    }, NOTIFICATION_REFRESH_INTERVAL_MS);

    const handleNotificationRefresh = () => {
      fetchNotifications(token);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications(token);
      }
    };

    window.addEventListener('focus', handleNotificationRefresh);
    window.addEventListener('routeplanner:inbox-refresh', handleNotificationRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleNotificationRefresh);
      window.removeEventListener('routeplanner:inbox-refresh', handleNotificationRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token]);

  useEffect(() => {
    let active = true;

    const loadProfileImage = async () => {
      if (!user?.profileImage) {
        if (active) setProfileImageSrc('');
        return;
      }

      try {
        const secureUrl = await resolveSecureFileUrl(user.profileImage, token);
        if (active) setProfileImageSrc(secureUrl);
      } catch (error) {
        console.error('Failed to load secure profile image:', error);
        if (active) setProfileImageSrc('');
      }
    };

    loadProfileImage();
    return () => {
      active = false;
    };
  }, [token, user?.profileImage]);

  // Upload profile image
  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const uploadRes = await fetch(`${process.env.REACT_APP_API_URL}/profile/upload`, {
      method: "POST",
      body: formData
    });

    const { url } = await uploadRes.json();
    if (!url) return;

    // Save URL in database
    await fetch(`${process.env.REACT_APP_API_URL}/profile/update-profile-image/${user.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ url })
    });

    // Update UserContext
    setUser(prev => ({ ...prev, profileImage: url }));
  };

  return (
    <nav>
      {/* LEFT LOGO */}
      <img src={white} className='logo' alt='logo' />

      {/* CENTER NAV LINKS */}
      <ul>
        <li>
          <NavLink to='/' end className={`link-items ${(location.pathname === '/' || location.pathname.startsWith('/create-request') || location.pathname.startsWith('/full-details') || location.pathname.startsWith('/courier-details') || location.pathname.startsWith('/inbox')) ? 'active' : ''}`}>
            Request
          </NavLink>
        </li>
        <li>
          <NavLink to='/planner' className='link-items' activeclassname='active'>
            Planner
          </NavLink>
        </li>

        {user.role !== 'USER' && (
          <li>
            <NavLink to='/report' className='link-items' activeclassname='active'>
              Report
            </NavLink>
          </li>
        )}
      </ul>

      <div className="header-right">
        <div className="notification-wrap">
          <button type="button" className="notification-bell" onClick={toggleNotifications}>
            <FaBell />
            {notifications.filter((item) => !item.isRead).length > 0 && (
              <span className="notification-badge">
                {notifications.filter((item) => !item.isRead).length}
              </span>
            )}
          </button>

          <div
            ref={notificationsRef}
            className={`notification-menu ${notificationsOpen ? 'open' : ''}`}
          >
            <div className="notification-menu-header">
              <span>Notifications</span>
              {!!notifications.length && notifications.some((item) => !item.isRead) && (
                <button
                  type="button"
                  className="notification-mark-read"
                  onClick={markAllNotificationsRead}
                >
                  Mark all read
                </button>
              )}
            </div>
            {notifications.length ? (
              notifications.map((notification) => (
                <div key={notification.id} className={`notification-item ${notification.isRead ? '' : 'unread'}`}>
                  <p>{notification.title}</p>
                  <span>{notification.message}</span>
                </div>
              ))
            ) : (
              <div className="notification-empty">No notifications</div>
            )}
          </div>
        </div>

        {/* RIGHT AVATAR */}
        <div className="user-pic" onClick={toggleDropdown}>
          {profileImageSrc ? (
            <img src={profileImageSrc} alt="avatar" />
          ) : (
            <div className="avatar-letter">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
        </div>
      </div>

      {/* DROPDOWN */}
      <div ref={dropdownRef} className={`sub-menu-wrap ${dropdownOpen ? 'open-menu' : ''}`}>
        <div className='sub-menu'>
          <div className='user-content'>

            <div className="dropdown-avatar-wrapper">
              <div className="dropdown-avatar-container">
                {profileImageSrc ? (
                  <img src={profileImageSrc} className="dropdown-avatar-img" alt="profile" />
                ) : (
                  <div className="avatar-letter-big">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
              </div>

              <div className="edit-icon" onClick={handleEditClick}>
                <svg className="edit-svg" viewBox="0 0 24 24">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 
                    7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 
                    0l-1.83 1.83 3.75 3.75 1.84-1.82z"/>
                </svg>
              </div>

              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </div>

            <div className='user-info'>
              <p>
                {user.name ? user.name : ''}
                {user.role !== 'USER' && <span> ({user.role})</span>}
              </p>
              <p>{user.email}</p>
              <p>{user.type ? user.type : ''}</p>
              <p>{user.department ? user.department : ''}</p>

              {user.role === 'SUPER_ADMIN' && (
                <Link to='/create-admin' target='_blank'>Create Admin</Link>
              )}
            </div>
          </div>

          <hr />

          <button type="button" className='sub-menu-link saved-info-trigger' onClick={toggleSavedInfo}>
            <span className="sub-menu-icon">
              <i className="fa-regular fa-bookmark" />
            </span>
            <p>Saved Info</p>
            <span>{'>'}</span>
          </button>

          <div className='sub-menu-link' onClick={handleClick}>
            <img src={logout} alt='logout' />
            <p>Logout</p>
            <span>{'>'}</span>
          </div>
        </div>
      </div>

      <div ref={savedInfoRef} className={`saved-info-panel ${savedInfoOpen ? 'open' : ''}`}>
        <div className="saved-info-header">
          <span>Saved Info</span>
          <button type="button" className="saved-info-close" onClick={() => setSavedInfoOpen(false)}>
            x
          </button>
        </div>

        <div className="saved-info-tabs">
          <button
            type="button"
            className={`saved-info-tab ${
              activeSavedInfoTab === "individuals" ? "active" : ""
            }`}
            onClick={() => setActiveSavedInfoTab("individuals")}
          >
            Saved Individuals
          </button>

          <button
            type="button"
            className={`saved-info-tab ${
              activeSavedInfoTab === "addresses" ? "active" : ""
            }`}
            onClick={() => setActiveSavedInfoTab("addresses")}
          >
            Saved Addresses
          </button>
        </div>
        {activeSavedInfoTab === "individuals" && (
          <input
            type="text"
            className="saved-info-search"
            placeholder="Search saved individuals..."
            value={savedInfoSearch}
            onChange={(event) => setSavedInfoSearch(event.target.value)}
          />
        )}
        {activeSavedInfoTab === "individuals" && (
        <div className="saved-info-list">
          {filteredSavedIndividuals.length ? (
            filteredSavedIndividuals.map((individual) => (
              <label key={individual.id} className="saved-info-item">
                <div>
                  <strong>{individual.name}</strong>
                  <span>
                    {[individual.gender, getDobPreview(individual.dob), individual.idNumber, individual.contactNumber]
                      .filter(Boolean)
                      .join(' | ')}
                  </span>
                </div>
                  <div className="saved-info-actions">
                    <button
                      type="button"
                      className="saved-info-edit"
                      onClick={() => {
                        setEditingIndividual(individual);
                        setIndividualModalMode("edit");
                        setSavedInfoOpen(false);
                        setIndividualModalOpen(true);
                      }}
                    >
                      <FiEdit2 />
                    </button>

                <button
                  type="button"
                  className="saved-info-delete"
                  onClick={() => askDeleteSavedInfo(individual)}
                >
                  <FiTrash2 />
                </button>
                </div>
              </label>
            ))
          ) : (
            <div className="saved-info-empty">No saved individuals found.</div>
          )}
        </div>
        )}
        {activeSavedInfoTab === "individuals" && (
          <>
            <button
              type="button"
              className="saved-info-add-new"
              onClick={handleAddNewIndividual}
            >
              + Add New Individual
            </button>
          </>
        )}
      {activeSavedInfoTab === "addresses" && (
        <>
          <input
            type="text"
            className="saved-info-search"
            placeholder="Search saved addresses..."
            value={savedAddressSearch}
            onChange={(e) => setSavedAddressSearch(e.target.value)}
          />

          <div className="saved-info-list">
            {filteredSavedAddresses.length ? (
              filteredSavedAddresses.map((address) => (
                <div
                  key={address.id}
                  className="saved-info-item"
                >
                <div className="saved-address-info">
                  <strong className="saved-address-icon">
                    <FiMapPin />
                  </strong>

                  <strong className="saved-address-text">
                    {address.address}
                  </strong>
                </div>

                  <div className="saved-info-actions">

                    <button
                      type="button"
                      className="saved-info-edit"
                      onClick={() => {
                        setSavedInfoOpen(false); 
                        setEditingAddress(address);
                        setAddressModalMode("edit");
                        setAddressModalOpen(true);
                      }}
                    >
                      <FiEdit2 />
                    </button>

                    <button
                      type="button"
                      className="saved-info-delete"
                      onClick={() =>
                        setSavedAddressDeleteTarget(address)
                      }
                    >
                      <FiTrash2 />
                    </button>

                  </div>
                </div>
              ))
            ) : (
              <div className="saved-info-empty">
                No saved addresses found.
              </div>
            )}
          </div>

          <button
            type="button"
            className="saved-info-add-new"
            onClick={handleAddNewAddress}
          >
            + Add New Address
          </button>
        </>
      )}
      </div>

      {savedInfoDeleteTarget && (
        <div className="saved-info-modal-overlay" onClick={() => setSavedInfoDeleteTarget(null)}>
          <div className="saved-info-modal-card" onClick={(event) => event.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Delete saved info for {savedInfoDeleteTarget.name}?</p>
            <div className="saved-info-modal-actions">
              <button type="button" className="saved-info-cancel" onClick={() => setSavedInfoDeleteTarget(null)}>
                Cancel
              </button>
              <button type="button" className="saved-info-confirm" onClick={handleConfirmDeleteSavedInfo}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {savedAddressDeleteTarget && (
        <div
          className="saved-info-modal-overlay"
          onClick={() => setSavedAddressDeleteTarget(null)}
        >
          <div
            className="saved-info-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Confirm Delete</h3>

            <p>
              Delete this saved address?
              <br />
              <strong>{savedAddressDeleteTarget.address}</strong>
            </p>

            <div className="saved-info-modal-actions">
              <button
                type="button"
                className="saved-info-cancel"
                onClick={() => setSavedAddressDeleteTarget(null)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="saved-info-confirm"
                onClick={handleConfirmDeleteSavedAddress}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <IndividualModal
        isOpen={individualModalOpen}
        mode={individualModalMode}
        individual={editingIndividual}
        token={token}
        onClose={() => {
          setIndividualModalOpen(false);
          setEditingIndividual(null);
        }}
        onSaved={(updatedIndividuals) => {
          setSavedIndividuals(updatedIndividuals);

          localStorage.setItem(
            getSavedIndividualsStorageKey(user?.email),
            JSON.stringify(updatedIndividuals)
          );
        }}
        onDelete={() => {
          setIndividualModalOpen(false);
          askDeleteSavedInfo(editingIndividual);
        }}
      />
    <AddressModal
      isOpen={addressModalOpen}
      mode={addressModalMode}
      address={editingAddress}
      token={token}
      onClose={() => {
        setAddressModalOpen(false);
        setEditingAddress(null);
      }}
      onSaved={(updatedAddresses) => {
        setSavedAddresses(updatedAddresses);

        window.dispatchEvent(
          new CustomEvent("routeplanner:saved-addresses-updated", {
            detail: {
              savedAddresses: updatedAddresses,
            },
          })
        );
      }}
      onDelete={() => {
        setAddressModalOpen(false);
        setSavedAddressDeleteTarget(editingAddress);
        setEditingAddress(null);
      }}
    />
    </nav>
  );
};

export default Header;
